import api from '../lib/axios';
import type { ApiResponse } from '@maios/shared';
import type { IssueListItem } from './issueService';

// --- Types ---

export interface SprintItem {
  sprintItemId: string;
  sprintId: string;
  issueId: string;
  addedAt: string;
  issue: {
    id: string;
    issueId: string;
    title: string;
    issueType: string;
    issueTypeDef?: { issueTypeId: string; name: string; icon: string; color: string; customIconPath?: string | null } | null;
    priority: string;
    statusId: string;
    workflowStatus: { statusId: string; name: string; color: string; category: string; sortOrder: number };
    storyPoints: number | null;
    // CHG-133 Step 6: 표시 일관성
    assignee: {
      id: string;
      name: string;
      avatarUrl: string | null;
      displayColor?: string | null;
      nickname?: string | null;
      firstName?: string | null;
      lastName?: string | null;
    } | null;
  };
}

export interface Sprint {
  sprintId: string;
  projectId: string;
  name: string;
  goal: string | null;
  status: string;
  startDate: string | null;
  endDate: string | null;
  duration: number | null;
  order: number;
  createdAt: string;
  updatedAt: string;
  items: SprintItem[];
  _count: { items: number };
}

export interface SprintListItem {
  sprintId: string;
  projectId: string;
  name: string;
  goal: string | null;
  status: string;
  startDate: string | null;
  endDate: string | null;
  duration: number | null;
  order: number;
  createdAt: string;
  updatedAt: string;
  items: SprintItem[];
  _count: { items: number };
}

export interface CreateSprintData {
  name?: string;
  goal?: string;
  startDate?: string;
  endDate?: string;
  duration?: number;
}

export interface UpdateSprintData {
  name?: string;
  goal?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  duration?: number | null;
}

export interface CompleteSprintData {
  carryOverToSprintId?: string;
}

// --- Sprints API ---

export async function fetchSprints(projectId: string) {
  const res = await api.get<ApiResponse<SprintListItem[]>>(
    `/projects/${projectId}/sprints`,
  );
  return res.data.data!;
}

export async function createSprint(projectId: string, data?: CreateSprintData) {
  const res = await api.post<ApiResponse<SprintListItem>>(
    `/projects/${projectId}/sprints`,
    data ?? {},
  );
  return res.data.data!;
}

export async function updateSprint(
  projectId: string,
  sprintId: string,
  data: UpdateSprintData,
) {
  const res = await api.patch<ApiResponse<SprintListItem>>(
    `/projects/${projectId}/sprints/${sprintId}`,
    data,
  );
  return res.data.data!;
}

export async function deleteSprint(projectId: string, sprintId: string) {
  await api.delete(`/projects/${projectId}/sprints/${sprintId}`);
}

export async function startSprint(projectId: string, sprintId: string) {
  const res = await api.post<ApiResponse<SprintListItem>>(
    `/projects/${projectId}/sprints/${sprintId}/start`,
  );
  return res.data.data!;
}

export async function completeSprint(
  projectId: string,
  sprintId: string,
  data?: CompleteSprintData,
) {
  const res = await api.post<ApiResponse<SprintListItem>>(
    `/projects/${projectId}/sprints/${sprintId}/complete`,
    data ?? {},
  );
  return res.data.data!;
}

// --- Sprint Items API ---

export async function addItemToSprint(
  projectId: string,
  sprintId: string,
  issueId: string,
) {
  const res = await api.post<ApiResponse<SprintItem>>(
    `/projects/${projectId}/sprints/${sprintId}/items`,
    { issueId },
  );
  return res.data.data!;
}

export async function addBulkItemsToSprint(
  projectId: string,
  sprintId: string,
  issueIds: string[],
) {
  const res = await api.post<ApiResponse<{ added: number; skipped: number }>>(
    `/projects/${projectId}/sprints/${sprintId}/items/bulk`,
    { issueIds },
  );
  return res.data.data!;
}

export async function removeItemFromSprint(
  projectId: string,
  sprintId: string,
  issueId: string,
) {
  await api.delete(
    `/projects/${projectId}/sprints/${sprintId}/items/${issueId}`,
  );
}

// --- Backlog API ---

export async function fetchBacklogIssues(
  projectId: string,
  params?: {
    issueType?: string[];
    priority?: string[];
    assigneeId?: string;
  },
) {
  const query = new URLSearchParams();
  if (params?.issueType?.length) query.set('issueType', params.issueType.join(','));
  if (params?.priority?.length) query.set('priority', params.priority.join(','));
  if (params?.assigneeId) query.set('assigneeId', params.assigneeId);
  const qs = query.toString();
  const res = await api.get<ApiResponse<IssueListItem[]>>(
    `/projects/${projectId}/sprints/backlog${qs ? `?${qs}` : ''}`,
  );
  return res.data.data!;
}
